import type AuthenticatedUser from 'interfaces/AuthenticatedUser';
import { ColorSchemePreferenceEnum } from '../../enums/ColorSchemePreferenceEnum';
import { UserProfileStatusEnum } from '../../enums/UserProfileStatusEnum';
import type User from '../../interfaces/User';
import type { OAuthProvider } from '../../interfaces/UserApiService';
import { clearPersistedActivateTotpInLocalStorage } from '../../utils/persistAppStoreActivateTotp';

export interface AccessibilityPreferences {
  highContrastEnabled: boolean;
  reduceMotionEnabled: boolean;
}

export interface CustomTokens {
  token: string;
  rToken: string;
  tokenExpiry?: number; // Unix timestamp in seconds
  rTokenExpiry?: number; // Unix timestamp in seconds
}

export interface AuthSlice {
  authenticatedUser: AuthenticatedUser | null;
  authenticatedUserEmail: string | null;
  /**
   * Hydration of `/user` profile relative to the session.
   * Session (token) can stay valid while profile is stale/unavailable — do not logout on 5xx.
   */
  userProfileStatus: UserProfileStatusEnum;
  token: string | null;
  locale: string | null;
  colorSchemePreference: ColorSchemePreferenceEnum;
  highContrastEnabled: boolean;
  reduceMotionEnabled: boolean;
  customTokens: CustomTokens | null;
  directOAuthProviders: OAuthProvider[] | null;
  onBehalfOfExpiresAt: number | null;
  onBehalfOfUser: User | null;
  /** Temporary token for TOTP setup during login (from partialAuthToken); not persisted */
  temporaryTotpToken: string | null;
  temporaryTotpTokenExpiresAt: string | null;
  /** Callback when temporary TOTP token expires (countdown or 401); invoked then cleared */
  onTemporaryTotpExpired: (() => void) | null;

  setAuthenticatedUser: (v: AuthenticatedUser | null) => void;
  setAuthenticatedUserEmail: (v: string | null) => void;
  setUserProfileStatus: (status: UserProfileStatusEnum) => void;
  setToken: (token: string | null) => void;
  setLocale: (locale: string | null) => void;
  setColorSchemePreference: (preference: ColorSchemePreferenceEnum) => void;
  setHighContrastEnabled: (enabled: boolean) => void;
  setReduceMotionEnabled: (enabled: boolean) => void;
  setCustomTokens: (tokens: CustomTokens | null) => void;
  setDirectOAuthProviders: (providers: OAuthProvider[] | null) => void;
  setOnBehalfOfData: (expiresAt: number | null, user: User | null) => void;
  clearOnBehalfOfData: () => void;
  setTemporaryTotpToken: (data: { token: string; expiresAt: string } | null) => void;
  clearTemporaryTotpToken: () => void;
  setOnTemporaryTotpExpired: (cb: (() => void) | null) => void;
  clearStoreAuthenticationData: () => void;
  /** Clears all store state on logout, preserving only locale and authenticatedUserEmail */
  resetStoreOnLogout: () => void;
}

export const createAuthSlice = (
  set: (state: any) => void,
  get?: () => AuthSlice & Record<string, any>,
): AuthSlice => ({
  authenticatedUser: null,
  authenticatedUserEmail: null,
  userProfileStatus: UserProfileStatusEnum.Loading,
  token: null,
  locale: null,
  colorSchemePreference: ColorSchemePreferenceEnum.Light,
  highContrastEnabled: false,
  reduceMotionEnabled: false,
  customTokens: null,
  directOAuthProviders: null,
  onBehalfOfExpiresAt: null,
  onBehalfOfUser: null,
  temporaryTotpToken: null,
  temporaryTotpTokenExpiresAt: null,
  onTemporaryTotpExpired: null,
  setAuthenticatedUser: (v) => set({ authenticatedUser: v }),
  setAuthenticatedUserEmail: (v) => set({ authenticatedUserEmail: v }),
  setUserProfileStatus: (status) => set({ userProfileStatus: status }),
  setToken: (token) => set({ token }),
  setLocale: (locale) => set({ locale }),
  setColorSchemePreference: (preference) => set({ colorSchemePreference: preference }),
  setHighContrastEnabled: (enabled) => set({ highContrastEnabled: enabled }),
  setReduceMotionEnabled: (enabled) => set({ reduceMotionEnabled: enabled }),
  setCustomTokens: (tokens) => set({ customTokens: tokens }),
  setDirectOAuthProviders: (providers) => set({ directOAuthProviders: providers }),
  setOnBehalfOfData: (expiresAt, user) =>
    set({ onBehalfOfExpiresAt: expiresAt, onBehalfOfUser: user }),
  clearOnBehalfOfData: () => set({ onBehalfOfExpiresAt: null, onBehalfOfUser: null }),
  setTemporaryTotpToken: (data) =>
    set({
      temporaryTotpToken: data?.token ?? null,
      temporaryTotpTokenExpiresAt: data?.expiresAt ?? null,
    }),
  clearTemporaryTotpToken: () =>
    set({
      temporaryTotpToken: null,
      temporaryTotpTokenExpiresAt: null,
      onTemporaryTotpExpired: null,
    }),
  setOnTemporaryTotpExpired: (cb) => set({ onTemporaryTotpExpired: cb }),
  clearStoreAuthenticationData: () =>
    set({
      authenticatedUser: null,
      token: null,
      customTokens: null,
      directOAuthProviders: null,
      onBehalfOfExpiresAt: null,
      onBehalfOfUser: null,
      userProfileStatus: UserProfileStatusEnum.Loading,
      // Preserve only locale and authenticatedUserEmail (for next login / i18n)
    }),
  resetStoreOnLogout: () => {
    if (!get) return;
    const state = get();
    state.clearStoreAuthenticationData();
    state.resetActivateTotpOnLogout?.();
    state.resetFilePasswordsOnLogout?.();
    state.resetFolderContentStatsOnLogout?.();
    state.clearAll?.();
    state.clearAllFilesThumbnails?.();
    state.setGrants?.([]);
    state.setPendingGrants?.([]);
    state.resetAlerts?.();
    state.resetFileDownloadState?.();
    state.resetRightPanelState?.();
    clearPersistedActivateTotpInLocalStorage();
  },
});
